import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { StoreProfileDetail, WalkthroughMarker } from '../../types';
import type { SpatialEntity } from '../../lib/spatialEntity';
import { buildSpatialEntities, resolveWalkthroughSpatialEntities } from '../../lib/spatialEntity';
import { WalkthroughVideoPlayer } from '../WalkthroughVideoPlayer';
import { FloorplanCanvas } from '../FloorplanCanvas';
import { buildZoneOutlineColors } from '../../lib/instoreLocationGroups';
import { cacheBustUrl } from '../../lib/cacheBustUrl';

interface RetailerWalkthroughTourProps {
  profile: StoreProfileDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialWalkId?: string;
  onFocusArea?: (kind: SpatialEntity['kind'], id: string) => void;
}

export function RetailerWalkthroughTour({
  profile,
  open,
  onOpenChange,
  initialWalkId,
  onFocusArea,
}: RetailerWalkthroughTourProps) {
  const walkthroughs = profile.walkthroughs;
  const [selectedWalkId, setSelectedWalkId] = useState<string | undefined>(initialWalkId ?? walkthroughs[0]?.id);
  const [activeMarker, setActiveMarker] = useState<WalkthroughMarker | null>(null);
  const [routeIndex, setRouteIndex] = useState(0);

  useEffect(() => {
    if (open && initialWalkId) {
      setSelectedWalkId(initialWalkId);
    }
  }, [open, initialWalkId]);

  const selectedWalk = walkthroughs.find((walk) => walk.id === selectedWalkId) ?? walkthroughs[0];
  const entities = useMemo(() => buildSpatialEntities(profile), [profile.aisles, profile.floorplanZones]);
  const routeEntities = useMemo(() => {
    if (!selectedWalk) return [];
    return resolveWalkthroughSpatialEntities(selectedWalk, entities, profile.floorplanZones);
  }, [selectedWalk, entities, profile.floorplanZones]);

  useEffect(() => {
    setRouteIndex(0);
    setActiveMarker(selectedWalk?.timelineMarkers[0] ?? null);
  }, [selectedWalk?.id]);

  useEffect(() => {
    // The tour stays mounted while closed. Do not keep pushing its route
    // selection into the parent map when the dialog is not active.
    if (!open || routeEntities.length === 0) return;
    const entity = routeEntities[Math.min(routeIndex, routeEntities.length - 1)];
    if (entity) onFocusArea?.(entity.kind, entity.id);
  }, [open, routeIndex, routeEntities, onFocusArea]);

  const activeRouteEntity = routeEntities[Math.min(routeIndex, Math.max(routeEntities.length - 1, 0))];
  const floorplanDisplayUrl = cacheBustUrl(profile.floorplanImageUrl, profile.floorplanImageVersion);
  const zoneOutlineColors = buildZoneOutlineColors(profile.floorplanZones);

  if (!selectedWalk) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Store tour</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {walkthroughs.length > 1 && (
            <Select value={selectedWalk.id} onValueChange={setSelectedWalkId}>
              <SelectTrigger aria-label="Choose walk-through">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {walkthroughs.map((walk) => (
                  <SelectItem key={walk.id} value={walk.id}>
                    {walk.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <WalkthroughVideoPlayer
              walk={selectedWalk}
              activeMarker={activeMarker}
              onMarkerChange={setActiveMarker}
              onMarkerClick={(marker) => {
                setActiveMarker(marker);
                const markerIndex = selectedWalk.timelineMarkers.findIndex((item) => item.id === marker.id);
                if (markerIndex >= 0 && routeEntities.length > 0) {
                  const mappedIndex = Math.min(
                    markerIndex,
                    routeEntities.length - 1,
                  );
                  setRouteIndex(mappedIndex);
                }
              }}
            />

            <div className="rounded-lg border p-2 min-h-[280px]">
              <p className="text-xs font-medium text-gray-500 mb-2 px-1">Route on floorplan</p>
              <FloorplanCanvas
                zones={profile.floorplanZones}
                aisles={profile.aisles}
                imageUrl={floorplanDisplayUrl}
                selectedZoneId={activeRouteEntity?.kind === 'zone' ? activeRouteEntity.id : null}
                selectedAisleId={activeRouteEntity?.kind === 'aisle' ? activeRouteEntity.id : null}
                zoneOutlineColors={zoneOutlineColors}
                minimalOverlays
                mapOverlayPoints={
                  activeRouteEntity
                    ? [{
                      id: `route-${activeRouteEntity.id}`,
                      kind: 'video',
                      x: activeRouteEntity.centerX,
                      y: activeRouteEntity.centerY,
                      label: selectedWalk.title,
                      spatialEntityId: activeRouteEntity.id,
                      spatialEntityKind: activeRouteEntity.kind,
                    }]
                    : []
                }
              />
              {routeEntities.length > 1 && (
                <div className="flex flex-wrap gap-1.5 mt-2 px-1">
                  {routeEntities.map((entity, index) => (
                    <Button
                      key={`${entity.kind}-${entity.id}`}
                      type="button"
                      size="sm"
                      variant={index === routeIndex ? 'default' : 'outline'}
                      onClick={() => setRouteIndex(index)}
                    >
                      {entity.label}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
